import './style.scss';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { openDiscountSignupModal, closeDiscountSignupModal, DiscountSignupModalState } from './state';
import AloModal from '../YogaUI/alo-ui/AloModal';
import { getCookie, setCookie } from 'src/scripts/utils/cookies';
import { MUI_MOBILE_BREAKPOINT } from 'src/scripts/utils/constants';
import { InitialUrlSearchParams } from 'src/scripts/utils/environment';
import { heapTrack, IS_NATIVE_APP_CLIENT } from 'src/scripts/utils/helpers';

const {
  wizEndpoint,
  discountSignup: {
    enabled: modalIsEnabledInAdmin,
    backgroundImageDesktop,
    backgroundImageMobile,
    heading,
    description,
    submitCta,
    declineCta,
    signupAgreement,
    termsUrl,
    successMessage,
    discountCode,
    customerTag,
  },
} = window.getSiteSettings();

let DiscountSignupModal = () => {
  const [hasFormSubmitted, setHasFormSubmitted] = useState(false);

  useEffect(() => {
    const areSignupModalCookiesPresent =
      getCookie('userDidDismissSignupModal') || getCookie('userDidCompleteSignupModal');
    const isMediumEmail = InitialUrlSearchParams.get('utm_medium')?.toLowerCase() === 'email';

    if (!IS_NATIVE_APP_CLIENT && !areSignupModalCookiesPresent && !isMediumEmail && modalIsEnabledInAdmin) {
      openDiscountSignupModal();
      trackSignupModalOpen(customerTag, discountCode);
    }
  }, []);

  const onModalCloseHandler = () => {
    closeDiscountSignupModal();
    if (hasFormSubmitted) {
      applyDiscountToCheckout();
    } else {
      trackSignupModalDismissWithoutSubmit(customerTag, discountCode);
      setCookie('userDidDismissSignupModal', '1', 30);
    }
  };

  return (
    <AloModal
      wrapperClass={`DiscountSignupModal`}
      disableBackdropClick={false}
      open={DiscountSignupModalState.isOpen}
      showCloseButton={false}
      onClose={() => onModalCloseHandler()}
      onOpen={openDiscountSignupModal}
    >
      <ModalContent
        hasFormSubmitted={hasFormSubmitted}
        setHasFormSubmitted={setHasFormSubmitted}
        onModalCloseHandler={onModalCloseHandler}
      />
    </AloModal>
  );
};

DiscountSignupModal = observer(DiscountSignupModal);
export { DiscountSignupModal };

const ModalContent = ({ hasFormSubmitted, setHasFormSubmitted, onModalCloseHandler }: IModalContent) => {
  const isMobile = useMediaQuery(MUI_MOBILE_BREAKPOINT);

  const onFormSubmitHandler = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const body = convertFormToPostBody(form);
    fetch(`${wizEndpoint}/offer-enroll`, {
      body,
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
      mode: 'cors',
    })
      .then((response) => {
        if (response.ok && response.status === 200) {
          setHasFormSubmitted(true);
          setCookie('userDidCompleteSignupModal', '1', 30);
        } else {
          console.log('Something went wrong with submitting to wizEndpoint', response);
        }
        return response.json();
      })
      .then((res) => {
        trackSignupModalSuccess(customerTag, discountCode, res.action);
      })
      .catch((error) => {
        console.log({ error });
      });
  };

  return (
    <div id="discount-signup-modal">
      {isMobile ? (
        <img className="mobile background" src={backgroundImageMobile} alt="alo-background" />
      ) : (
        <img className="desktop background" src={backgroundImageDesktop} alt="alo-background" />
      )}
      <div className={`alo-modal-header`}>
        <img
          id="modal-logo"
          src="https://cdn.shopify.com/s/files/1/2449/4349/files/alo-logo-black.png?v=1636046902"
          alt="alo-logo"
        />
        <div role="button" className="alo-modal-close-icon" onClick={onModalCloseHandler} />
      </div>
      <h2 id="modal-title">{heading}</h2>
      {hasFormSubmitted ? (
        <div id="coupon-content" className="content-container">
          <p className="modal-message">{successMessage}</p>
          <div id="coupon-container">
            <p>{discountCode}</p>
          </div>
        </div>
      ) : (
        <div id="signup-content" className="content-container visible">
          <p className="modal-message">{description}</p>
          <form id="discount-signup-form" className="d-flex" method="post" onSubmit={onFormSubmitHandler}>
            <input type="email" autoComplete="email" id="discount-signup-email" name="email" placeholder="Email" />
            <input type="text" name="accepts_marketing" value="1" hidden />
            <input type="text" name="tags" value={customerTag} hidden />
            <input type="submit" id="discount-signup-button" value={submitCta} />
          </form>
          <button id="discount-decline-link" className="tiny" onClick={onModalCloseHandler}>
            {declineCta}
          </button>
          <TermsOfService />
        </div>
      )}
    </div>
  );
};

const TermsOfService = () => {
  const tos = 'Terms of Service';
  if (signupAgreement?.toLowerCase()?.includes(tos.toLowerCase())) {
    const [firstPart, lastPart] = signupAgreement.split(tos);
    return (
      <p id="terms">
        {firstPart}
        <a href={termsUrl} target="_blank" rel="noreferrer" id="terms-of-service-link">
          {tos}
        </a>
        {lastPart}
      </p>
    );
  }
  return <p id="terms">{signupAgreement}</p>;
};

const applyDiscountToCheckout = () => {
  if (discountCode) {
    const redirectUrl = window.encodeURIComponent(window.location.pathname);
    window.location.replace(`/discount/${discountCode}?redirect=${redirectUrl}`);
  }
};

const convertFormToPostBody = (form: HTMLFormElement) => {
  const formData = new FormData(form);
  const emailCapture = formData.get('email');
  const tagsCapture = formData.get('tags');
  const acceptsMarketing = formData.get('accepts_marketing');
  const requestBody = {
    email: emailCapture,
    tags: tagsCapture ? tagsCapture.toString().split(',') : [],
    accepts_marketing: acceptsMarketing,
  };
  return JSON.stringify(requestBody);
};

const trackSignupModalOpen = (tag: string, code: string = 'None') => {
  heapTrack('General - Present - Email Offer Modal', { 'Modal Customer Tag': tag, 'Modal Discount Code': code });
};
const trackSignupModalSuccess = (tag: string, code: string = 'None', result: string) => {
  // result should be the parameter "action" returned by wiz endpoint, usually 'new' or 'updated'
  heapTrack('General - Submit - Email in Offer Modal', {
    'Modal Customer Tag': tag,
    'Modal Discount Code': code,
    'Modal Signup Customer Result': result,
  });
};

const trackSignupModalDismissWithoutSubmit = (tag: string, code: string = 'None') => {
  heapTrack('General - Dismiss - Email Offer Modal', { 'Modal Customer Tag': tag, 'Modal Discount Code': code });
};

interface IModalContent {
  hasFormSubmitted: boolean;
  setHasFormSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  onModalCloseHandler: () => void;
}
